import Exim from 'exim';
import _ from 'lodash';
import { observable } from 'mobx';
import Api from 'tm3-core/api';
import type { FolderTree } from 'tm3-core/api.dtos';
import { cache } from 'tm3-core/cache';
import { TemplatesUpdatedEvent } from 'tm3-core/events/template';
import lazy from './lazy-loader';

type TTemplate = {
  templates: FolderTree;
  filteredTemplates: FolderTree;
};

const TemplateObservable: TTemplate = observable({
  templates: {
    folders: [],
    files: []
  },
  filteredTemplates: {
    files: [],
    folders: []
  }
});

const Store = Exim.createStore({
  actions: [
    'setCurrent',
    'loadAll',
    'loadAttachments',
    'setTemplates',
    'delete',
    'rename',
    'copy',
    'reloadTemplates',
    'reset'
  ],
  initial: {
    loadingTemplates: true,
    templates: {
      folders: [],
      files: []
    },
    filteredTemplates: {
      folders: [],
      files: []
    }
  },

  setCurrent(name, filter) {
    return lazy('/api/templates').then((res) => {
      const templatesFolder = res.body;
      let filteredTemplates = { folders: [], files: [] };

      if (name && templatesFolder.folders) {
        filteredTemplates = templatesFolder.folders.find((x) => x.name === name) || {
          folders: [],
          files: []
        };
      }

      this.set({ filteredTemplates });
      TemplateObservable.templates = res.body; // Handle refresh issue (legacy)
      TemplateObservable.filteredTemplates = filteredTemplates;
    });
  },

  reloadTemplates() {
    lazy.clear('/api/templates');
    return this.actions.loadAll();
  },

  loadAll() {
    const templateState = this.state?.templates ?? { folders: [], files: [] };
    const hasTemplateData =
      (templateState.folders?.length || 0) > 0 || (templateState.files?.length || 0) > 0;

    this.set({
      loadingTemplates: !hasTemplateData
    });

    return lazy('/api/templates')
      .then((res) => {
        TemplateObservable.templates = res.body;
        return this.set({
          templates: res.body,
          loadingTemplates: false
        });
      })
      .catch((error) => {
        console.log('Error loading templates', error);
        return this.set({
          loadingTemplates: false
        });
      });
  },

  loadAttachments(mode) {
    const templateState = this.state?.templates ?? { folders: [], files: [] };
    const hasTemplateData =
      (templateState.folders?.length || 0) > 0 || (templateState.files?.length || 0) > 0;

    this.set({
      loadingTemplates: !hasTemplateData
    });

    return lazy('/api/templates')
      .then((res) => {
        return this.set({
          templates: res.body,
          loadingTemplates: false
        });
      })
      .catch((error) => {
        console.log('Error loading templates', error);
        return this.set({
          loadingTemplates: false
        });
      });
  },

  delete(path, isGlobal) {
    path = path.replace(/\\|\//g, '%2F');
    return Api.post('/api/delete', { path, isGlobal }).then((res) => {
      return this.actions.reloadTemplates().then(() => {
        return cache.mediator.publish(new TemplatesUpdatedEvent());
      });
    });
  },

  rename(path, newName, isGlobal) {
    if (isGlobal) {
      this.actions.rename(path, path + '.hidden');
    }
    path = path.replace(/\\|\//g, '%2F');
    newName = newName.replace(/\\|\//g, '%2F');
    return Api.post('/api/rename', { path, newName, overwrite: true, isGlobal }).then((res) => {
      return this.actions.reloadTemplates().then(() => {
        return cache.mediator.publish(new TemplatesUpdatedEvent());
      });
    });
  },

  copy(path, newName, isGlobal) {
    path = path.replace(/\\|\//g, '%2F');
    newName = newName.replace(/\\|\//g, '%2F');
    return Api.post('/api/copy', { path, newName, overwrite: true, isGlobal }).then((res) => {
      return this.actions.reloadTemplates().then(() => {
        return cache.mediator.publish(new TemplatesUpdatedEvent());
      });
    });
  }
});

// Store.TemplateObservable = TemplateObservable
// module.exports = Store

export default Store;
export { TemplateObservable };
